/**
 * CompanionWidget - Widget du compagnon virtuel ADHD-friendly
 *
 * Affiche le compagnon actif avec:
 * - Avatar et nom
 * - Message d'encouragement contextuel
 * - Sélecteur de compagnon
 */

import React, { memo, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check, Sparkles } from 'lucide-react';

const CompanionWidget = memo(function CompanionWidget({
  companion,
  companions,
  message,
  onSelectCompanion,
  onRequestMessage,
  energy = 3,
  compact = false,
}) {
  const [showSelector, setShowSelector] = useState(false);

  const handleSelect = useCallback((id) => {
    onSelectCompanion?.(id);
    setShowSelector(false);
  }, [onSelectCompanion]);

  const handleTap = useCallback(() => {
    onRequestMessage?.('encouragement', energy);
  }, [onRequestMessage, energy]);

  if (!companion) return null;

  // Mode compact pour mobile
  if (compact) {
    return (
      <motion.div
        className="flex items-center gap-2 px-3 py-2 bg-zinc-800/50 rounded-xl"
        whileTap={{ scale: 0.98 }}
        onClick={handleTap}
      >
        <span className="text-2xl">{companion.emoji}</span>
        <div className="flex-1 min-w-0">
          <p className="text-xs text-zinc-300 truncate">{message || companion.greetings?.[0]}</p>
        </div>
      </motion.div>
    );
  }

  return (
    <div className="relative">
      {/* Widget principal */}
      <motion.div
        className="p-4 rounded-2xl border transition-colors"
        style={{
          backgroundColor: `${companion.color}10`,
          borderColor: `${companion.color}30`,
        }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Header avec avatar et sélecteur */}
        <div className="flex items-center justify-between mb-3">
          <motion.button
            className="flex items-center gap-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowSelector(!showSelector)}
          >
            <span className="text-3xl">{companion.emoji}</span>
            <div>
              <span className="text-sm font-semibold" style={{ color: companion.color }}>
                {companion.name}
              </span>
              <ChevronDown
                className={`w-3 h-3 ml-1 inline transition-transform ${showSelector ? 'rotate-180' : ''}`}
                style={{ color: companion.color }}
              />
            </div>
          </motion.button>

          <motion.button
            className="p-2 rounded-lg transition-colors"
            style={{ backgroundColor: `${companion.color}20` }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleTap}
            title="Obtenir un message"
          >
            <Sparkles className="w-4 h-4" style={{ color: companion.color }} />
          </motion.button>
        </div>

        {/* Message du compagnon */}
        <AnimatePresence mode="wait">
          <motion.div
            key={message}
            initial={{ opacity: 0, y: 5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            className="bg-zinc-900/50 rounded-xl p-3"
          >
            <p className="text-sm text-zinc-200 leading-relaxed">
              {message || companion.greetings?.[0] || 'Salut ! Je suis là pour toi !'}
            </p>
          </motion.div>
        </AnimatePresence>

        {/* Personnalité */}
        <p className="text-xs text-zinc-500 mt-2 italic">
          {companion.personality}
        </p>
      </motion.div>

      {/* Sélecteur de compagnon */}
      <AnimatePresence>
        {showSelector && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            className="absolute top-full left-0 right-0 mt-2 p-2 bg-zinc-900 border border-zinc-800 rounded-xl shadow-xl z-50"
          >
            <p className="text-xs text-zinc-500 px-2 mb-2">Choisis ton compagnon</p>
            <div className="space-y-1">
              {Object.values(companions).map((comp) => (
                <motion.button
                  key={comp.id}
                  className={`w-full flex items-center gap-3 p-2 rounded-lg transition-colors ${
                    comp.id === companion.id
                      ? 'bg-zinc-800'
                      : 'hover:bg-zinc-800/50'
                  }`}
                  whileHover={{ x: 2 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleSelect(comp.id)}
                >
                  <span className="text-2xl">{comp.emoji}</span>
                  <div className="flex-1 text-left">
                    <span className="text-sm font-medium text-zinc-200">{comp.name}</span>
                    <span className="text-xs text-zinc-500 block">{comp.personality}</span>
                  </div>
                  {comp.id === companion.id && (
                    <Check className="w-4 h-4" style={{ color: comp.color }} />
                  )}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

export { CompanionWidget };
